import { Form, Formik, getIn } from 'formik';
import React, { useContext, useEffect, useRef, useState } from 'react';
import Moment from 'react-moment';
import * as Yup from 'yup';

import { useParams } from '@gatsbyjs/reach-router';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';

import Confirm from '../../../components/confirm';
import ConfirmSelect from '../../../components/confirm-select';
import DisplayAddressResult from '../../../components/display-address-result';
import SaveBackdrop from '../../../components/saveBackdrop';
import Screen from '../../../components/screen';
import SplashWait from '../../../components/splash-wait';
import { AppContext } from '../../../contexts/app-context';
import GlobalStyles from '../../../global-styles';
import API from '../../../utils/api';
import {
  addressValidation, apiReponseError, apiRequestError, canReadKit, canReadProgram,
  canWriteCompany, canWriteProgram, compareArrayObjectValue, filterCountryStates, generateUUID,
  getBreadcrumbMaxItems, getDefaultAddressResult, getErrorObject, getProgramDetail, goToRoutePage,
  isFormReferenceValid, isNullOrWhiteSpace, isValidJSONString, openExternalLink,
  redirectRoutePage, setWindowTitle
} from '../../../utils/helper';
import {
  AddIcon, ClearAllIcon, ClearIcon, DeleteIcon, FiberNewIcon, MetadataIcon, MoreIcon,
  OpenInNewIcon, ValidateAddressIcon
} from '../../../utils/icons';
import {
  Alert, Box, Breadcrumbs, Button, Card, CardContent, CardHeader, Divider, FormControl,
  FormControlLabel, FormGroup, FormHelperText, FormLabel, Grid, IconButton, Input, InputAdornment,
  InputLabel, LinearProgress, Link, ListItem, ListItemIcon, ListItemText, Menu, Paper, Select,
  Switch, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip,
  Typography
} from '../../../utils/material-ui-core';
import LegacyEnlistmentContentsCURD from './legacyEnlistmentContents-curd';
import ProgramThemeAndMetadataCRUD from './programThemeAndMetadata-curd';

// Default form value
const formDefaultValue: Program = {
  active: false,
  address: {
    addressline1: "",
    addressline2: "",
    addressline3: "",
    country: "USA",
    locality1: "",
    locality2: "",
    postalCode: "",
    region1: "",
    region2: "",
  },
  aemSiteId: "",
  autoEnroll: false,
  defaultPackageId: 0,
  enableEnlistment: false,
  enableSubscriptionBilling: false,
  enrollmentCount: 0,
  folderId: 0,
  clientCompanyKey: "",
  lastModifiedBy: "",
  lastModifiedDateTime: "",
  enlistmentSiteManageByAEM: false,
  maxAccountCanBeRegister: 0,
  meshifyDomainName: "",
  parentName: "",
  partnerDCXSiteId: "",
  programCode: 0,
  programKey: "",
  programName: "",
  programPartnerId: "",
  routingName: "",
  sendToPSFIN: false,
  shopifyLandingPage: "",
  showAgreementConfirmation: false,
  totalPilotAccountForRegistration: 0,
  uniqueDCXSite: false,
  showInEnlistmentProductLookup: false,
  enlistmentProgramGroupCode: "",
  isEnlistmentCustomerSite: false,
  enableParentEnlistment: false,
  parentRoutingName: "",
  readyToEnroll: false,
  readyToEnrollApprovedBy: "",
  readyToEnrollApprovedDate: "",
  taxExempt: true,
  showKits: false,
  whiskerGroupId: 0,
  whiskerGroupType: ""
};

const formDefaultTouchValue = {
  active: true,
  addressline1: true,
  addressline2: false,
  programName: true,
  aemSiteId: true,
  locality1: true,
  locality2: false,
  region1: true,
  region2: false,
};

const formValidationSchema = Yup.object().shape({
  programName: Yup.string().required("Name is required."),
  clientCompanyKey: Yup.string().required("Company is required."),
  address: Yup.object().shape({
    addressline1: Yup.string().required("Address line 1 is required."),
    locality1: Yup.string().required("City is required."),
    region1: Yup.string().required("State is required."),
    postalCode: Yup.string().required("Postal code is required."),
    country: Yup.string().required("Country is required."),
  }),
  enableEnlistment: Yup.boolean().required("Enable enlistment is required"),
  routingName: Yup.string()
    .trim()
    .when(["enableEnlistment"], {
      is: (enableEnlistment) => {
        return enableEnlistment;
      },
      then: Yup.string().required("AEM site prefix is required."),
    }),
  // meshifyDomainName: Yup.string()
  //   .trim()
  //   .when(["showInEnlistmentProductLookup"], {
  //     is: (showInEnlistmentProductLookup) => {
  //       return showInEnlistmentProductLookup;
  //     },
  //     then: Yup.string().required("Meshify domain name is required."),
  //   }),    
  defaultPackageId: Yup.number().moreThan(-1, "Default package must be greater than or equal to zero."),
  maxAccountCanBeRegister: Yup.number().moreThan(-1, "Max location register must be greater than or equal to zero."),
  totalPilotAccountForRegistration: Yup.number().moreThan(-1, "Total pilot locations must be greater than or equal to zero."),
  aemSiteId: Yup.string().when(["enableEnlistment"], {
    is: (enableEnlistment) => {
      return enableEnlistment;
    },
    then: Yup.string().required("AEM site id is required."),
  }),
});

const defaultAddressResultOutput: AddressResultOutput = getDefaultAddressResult();
const initialAddressResultOutput: AddressResultOutput = getDefaultAddressResult();

const ProgramCRUD: React.FC = () => {
  const pageStyles = LocalPageStyles();
  const globalStyles = GlobalStyles();

  const { refreshData, showAlert, clientCompanies, kits, appSetting, referenceLookups, userFunctionAccess } = useContext(AppContext);

  const { programKey, clientCompanyKey } = useParams();

  const formReference = useRef(null) as any;

  const defaultProgramKit = {
    hsbKitNo: 0,
    kitKey: "",
    kitProgramKey: "",
    name: "",
    used: false,
    active: false,
  } as ProgramKit;

  const [addKitConfirmOpen, setAddKitConfirmOpen] = useState<boolean>(false);
  const [addressValidationInProgress, setAddressValidationInProgress] = useState<boolean>(false);
  const [anchorElForOptionsMenu, setAnchorElForOptionsMenu] = useState<null | HTMLElement>(null);
  const [deleteConfirmDialogOpenForProgramKit, setDeleteConfirmDialogOpenForProgramKit] = useState<boolean>(false);
  const [folderId, setFolderId] = useState<number>(0);
  const [initialFormValues, setInitialFormValues] = useState<Program>(formDefaultValue);
  const [isNew, setIsNew] = useState<boolean>(false);
  const [limitedUpdates, setLimitedUpdates] = useState<boolean>(false);
  const [isPageInitialize, setIsPageInitialize] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [newKitLookupValues, setNewKitLookupValues] = useState<Lookup[]>([]);
  const [programAddressResultOutput, setProgramAddressResultOutput] = useState<AddressResultOutput>(initialAddressResultOutput);
  const [programKits, setProgramKits] = useState<ProgramKit[]>([]);
  const [readonly, setReadonly] = useState<boolean>(true);
  const [saveButtonVisible, setSaveButtonVisible] = useState<boolean>(false);
  const [selectedProgramKit, setSelectedProgramKit] = useState({ arrayIndex: 0, programKit: defaultProgramKit, });
  const [states, setStates] = useState<State[]>([]);
  const [themeEditDialogOpen, setThemeEditDialogOpen] = useState<boolean>(false);
  const [legacyEnlistmentContentEditDialogOpen, setLegacyEnlistmentContentEditDialogOpen] = useState<boolean>(false);  
  const [saveInProgress, setSaveInProgress] = useState<boolean>(false);

  // Options menu related func
  const handleAnchorElOptionsMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorElForOptionsMenu(event.currentTarget);
  };

  const handleAnchorElOptionsMenuClose = () => {
    setAnchorElForOptionsMenu(null);
  };

  // Theme & Metadata
  const saveThemeAndMetadataResult = (confirmOutput: ConfirmOutput) => {
    setThemeEditDialogOpen(false);

    if (confirmOutput.status === "refresh") {
      redirectRoutePage("onboarding/programs/" + programKey);
    }
  };

  const openThemeEdit = () => {
    setThemeEditDialogOpen(true);
  };

  const openLegacyEnlistmentContentEdit = () => {
    setLegacyEnlistmentContentEditDialogOpen(true);
  };

  const saveLegacyEnlistmentContentResult = (confirmOutput: ConfirmOutput) => {
    setLegacyEnlistmentContentEditDialogOpen(false);

    if (confirmOutput.status === "refresh") {
      redirectRoutePage("onboarding/programs/" + programKey);
    }
  };

  // Enlistment

  const gotoEnlistment = (pathname: string) => {
    if (formReference.current.values.enableEnlistment) {
      const programRoutingName = formReference.current.values.routingName;
      const companyRoutingName: any = clientCompanies.find((company) => company.clientCompanyKey === formReference.current.values.clientCompanyKey)?.routingName;
      if (!isNullOrWhiteSpace(companyRoutingName) && !isNullOrWhiteSpace(programRoutingName)) {
        openExternalLink(appSetting.legacyEnlistmentUrl + companyRoutingName + "/" + programRoutingName + "/" + pathname);
      }
    }
  };

  const goToCompany = () => {
    if (!canWriteCompany(userFunctionAccess)) {
      return false;
    }
    const path = "onboarding/clientcompanies/" + formReference.current.values.clientCompanyKey;
    goToRoutePage(path);
  };

  const generateNewAEMSiteId = () => {
    const newId: string = generateUUID();
    formReference.current.setFieldValue("aemSiteId", newId);
    setSaveButtonVisible(true);
  };

  const clearAEMSiteId = () => {
    formReference.current.setFieldValue("aemSiteId", "");
    setSaveButtonVisible(true);
  };

  // Address related func

  const handleAddressChanged = () => {
    closeAddressMessageStatus();
  };

  const useThisAddress = () => {
    formReference.current.setFieldValue("address", programAddressResultOutput.address);

    if (programAddressResultOutput.address.country === "US") {
      formReference.current.setFieldValue("address.country", "USA");
    } else if (programAddressResultOutput.address.country === "CA") {
      formReference.current.setFieldValue("address.country", "CAN");
    }

    closeAddressMessageStatus();
    setSaveButtonVisible(true);
  };

  const resetAddressFields = () => {
    formReference.current.setFieldValue("address.addressline1", "");
    formReference.current.setFieldValue("address.addressline2", "");
    formReference.current.setFieldValue("address.locality1", "");
    formReference.current.setFieldValue("address.region1", "");
    formReference.current.setFieldValue("address.postalCode", "");
    formReference.current.setFieldTouched("address.addressline1", true);
    formReference.current.setFieldTouched("address.locality1", true);
    formReference.current.setFieldTouched("address.region1", true);
    formReference.current.setFieldTouched("address.postalCode", true);
    setTimeout(() => {
      formReference.current.validateForm();
    }, 100);
    setProgramAddressResultOutput(defaultAddressResultOutput);
    setSaveButtonVisible(true);
  };

  const handleCountryChanged = (event: any) => {
    setStates(filterCountryStates(referenceLookups.countries, event.target.value));
    formReference.current.setFieldValue("address.region1", "");
  };

  const closeAddressMessageStatus = () => {
    const addressResultOutput = JSON.parse(JSON.stringify(programAddressResultOutput));
    addressResultOutput.show = false;
    setProgramAddressResultOutput({ ...addressResultOutput });
  };

  const showAddressValidationButton = () => {
    if (isFormReferenceValid(formReference)) {
      if (formReference.current.values.address.addressline1 !== "" && formReference.current.values.address.locality1 !== "" && formReference.current.values.address.region1 !== "" && formReference.current.values.address.postalCode !== "" && formReference.current.values.address.country !== "") {
        return true;
      }
    }
    return false;
  };

  const checkAddress = async () => {
    const payload = {
      address: {
        addressline1: formReference.current.values.address.addressline1,
        addressline2: formReference.current.values.address.addressline2,
        addressline3: "",
        country: formReference.current.values.address.country,
        locality1: formReference.current.values.address.locality1,
        locality2: "",
        postalCode: formReference.current.values.address.postalCode,
        region1: formReference.current.values.address.region1,
        region2: "",
      },
      allowPOBox: false,
      firmName: "",
    };

    const addressResultOutput = JSON.parse(JSON.stringify(programAddressResultOutput));
    addressResultOutput.show = false;

    try {
      setAddressValidationInProgress(true);
      setProgramAddressResultOutput(addressResultOutput);

      const apiResponse = await addressValidation(payload);
      if (apiResponse.success) {
        if (isValidJSONString(apiResponse.data.address)) {
          const address = apiResponse.data.address;

          if (address.valid) {
            addressResultOutput.valid = address.valid;
            addressResultOutput.message = address.friendlyMessage;
            addressResultOutput.address = address;
            addressResultOutput.addressLine = address.addressline1 + " " + address.addressline2;
            addressResultOutput.cityStatePostalCountry = address.locality1 + " " + address.region1 + " " + address.postalCode + " " + address.country;
            addressResultOutput.errorCodeTagged = address.errorCodeTagged;
            addressResultOutput.latitude = address.latitude;
            addressResultOutput.longitude = address.longitude;
            addressResultOutput.poBox = address.poBox;
            addressResultOutput.autoCorrect = address.autoCorrect;
            addressResultOutput.sameAddressLine = address.sameAddressLine;
            addressResultOutput.addressType = address.addressType;
          } else {
            addressResultOutput.valid = false;
            addressResultOutput.message = address.friendlyMessage;
          }
        } else {
          addressResultOutput.valid = false;
        }
      } else {
        addressResultOutput.valid = false;
        addressResultOutput.message = apiResponse.errorMessage;
      }

      addressResultOutput.show = true;
      setProgramAddressResultOutput({ ...addressResultOutput });
      setAddressValidationInProgress(false);
    } catch (e) {
      addressResultOutput.valid = false;
      addressResultOutput.message = "Something was wrong in our system.";
      addressResultOutput.show = true;
      setProgramAddressResultOutput({ ...addressResultOutput });
      setAddressValidationInProgress(false);
    }
  };

  // Kits related func

  const selectNewKit = async (confirmOutput: ConfirmOutput) => {
    setAddKitConfirmOpen(false);

    if (confirmOutput.status === "select") {
      const kitKeys: string[] = [];
      confirmOutput.data.forEach((item) => {
        kitKeys.push(item.value);
      });

      const payload = {
        kitKeys: kitKeys,
      };

      try {
        setReadonly(true);
        await API.post("/api/onboarding/programs/" + programKey + "/kits", payload)
          .then(async (response) => {
            setLoading(true);
            refreshData("programs");
            redirectRoutePage("onboarding/programs/" + programKey);
            return;
          })
          .catch((error) => {
            setReadonly(false);
            showAlert({
              message: apiReponseError(error, "Failed to save program kits."),
              severity: "error",
            });
          });
      } catch (e) {
        setReadonly(false);
        showAlert({
          message: apiRequestError(e, "Something went wrong in our system."),
          severity: "error",
        });
      }
    }
  };

  const handlePackageKitItemClick = (props: ProgramKit) => {
    if (!canReadKit(userFunctionAccess)) {
      return false;
    }
    const path = "onboarding/kits/" + props.kitKey;
    goToRoutePage(path);
  };

  const showDeleteConfirmationForProgramKit = (arrayIndex: number, props: ProgramKit) => {
    setSelectedProgramKit({ arrayIndex: arrayIndex, programKit: props });
    setDeleteConfirmDialogOpenForProgramKit(true);
  };

  const handleAddNewKit = () => {
    const lookupValues: Lookup[] = [];

    const tempKits = kits.filter(x => x.active && x.subscriptionBilling === formReference.current.values.enableSubscriptionBilling);

    for (let i = 0; i < tempKits.length; i++) {
      if (programKits.find((programKit) => programKit.kitKey === tempKits[i].kitKey)?.kitKey === undefined) {
        lookupValues.push({
          value: tempKits[i].kitKey,
          name: tempKits[i].name + " - " + tempKits[i].hsbKitNo,
          checked: false,
        });
      }
    }

    setNewKitLookupValues(lookupValues.sort(compareArrayObjectValue("name", "asc")));
    setAddKitConfirmOpen(true);
  };

  const deleteConfirmationForProgramKit = async (deleteConfirm: boolean) => {
    setDeleteConfirmDialogOpenForProgramKit(false);

    if (deleteConfirm) {
      try {
        setReadonly(true);
        await API.delete("/api/onboarding/programs/" + programKey + "/kits/" + selectedProgramKit.programKit.kitKey)
          .then(async (response) => {
            setLoading(true);
            refreshData("programs");
            redirectRoutePage("onboarding/programs/" + programKey);
            return;
          })
          .catch((error) => {
            setReadonly(false);
            showAlert({
              message: apiReponseError(error, "Failed to remove program kit."),
              severity: "error",
            });
          });
      } catch (e) {
        setReadonly(false);
        showAlert({
          message: apiRequestError(e, "Something went wrong in our system."),
          severity: "error",
        });
      }
    }
  };

  // Form related func

  const saveProgram = async (values: Program) => {
    if (!canWriteProgram(userFunctionAccess)) {
      showAlert({ message: "Access Denied.", severity: "error" });
      goToRoutePage("onboarding");
      return;
    }

    const enableEnlistment = values.enableEnlistment;
    //let showInEnlistmentProductLookup = values.showInEnlistmentProductLookup;
    // let enlistmentSiteManageByAEM = values.enlistmentSiteManageByAEM;
    // if (!enableEnlistment) {
    //   showInEnlistmentProductLookup = false;
    // }

    // if (!showInEnlistmentProductLookup) {
    //   enlistmentSiteManageByAEM = false;
    // }


    const payload = {
      program: {
        active: values.active,
        address: {
          addressline1: values.address.addressline1,
          addressline2: values.address.addressline2,
          addressline3: "",
          country: values.address.country,
          locality1: values.address.locality1,
          locality2: values.address.locality2,
          postalCode: values.address.postalCode,
          region1: values.address.region1,
          region2: values.address.region2,
        },
        aemSiteId: enableEnlistment ? values.aemSiteId : "",
        autoEnroll: enableEnlistment ? values.autoEnroll : false,
        clientCompanyKey: values.clientCompanyKey,
        defaultPackageId: enableEnlistment ? values.defaultPackageId : 0,
        enableEnlistment: values.enableEnlistment,
        enableSubscriptionBilling: enableEnlistment ? values.enableSubscriptionBilling : false,
        enlistmentProgramGroupCode: enableEnlistment ? values.enlistmentProgramGroupCode : "",
        enlistmentSiteManageByAEM: enableEnlistment,
        maxAccountCanBeRegister: enableEnlistment ? values.maxAccountCanBeRegister : 0,
        isEnlistmentCustomerSite: enableEnlistment,
        meshifyDomainName: enableEnlistment ? values.meshifyDomainName : false,
        programCode: isNew ? 0 : values.programCode,
        programKey: values.programKey,
        programName: values.programName,
        routingName: enableEnlistment ? values.routingName : "",
        sendToPSFIN: values.sendToPSFIN,
        shopifyLandingPage: values.shopifyLandingPage,
        showAgreementConfirmation: enableEnlistment ? values.showAgreementConfirmation : false,
        showInEnlistmentProductLookup: enableEnlistment,
        totalPilotAccountForRegistration: enableEnlistment ? values.totalPilotAccountForRegistration : 0,
        taxExempt: values.taxExempt,
        showKits: values.showKits
      },
    };

    try {
      setSaveInProgress(true);
      await API.post("/api/onboarding/programs/" + values.programKey, payload)
        .then(async (response) => {
          showAlert({ message: "Successfully saved.", severity: "success" });
          refreshData("programs");
          redirectRoutePage("onboarding/programs/" + response.data.programKey);
          return;
        })
        .catch((error) => {
          setSaveInProgress(false);
          const errorList: ApiResponseErrorObject[] = getErrorObject(error, "program.");
          let isErrorAssignedAtFieldLevel = true;
          try {
            errorList.forEach(function (error) {
              formReference.current.setFieldError(error.title, error.message.join(" "));
            });
          } catch (e) {
            isErrorAssignedAtFieldLevel = false;
          }
          isErrorAssignedAtFieldLevel = false;
          if (errorList.length <= 0 || !isErrorAssignedAtFieldLevel) {
            showAlert({
              message: apiReponseError(error, "Failed to save program data."),
              severity: "error",
            });
          }
        });
    } catch (e) {
      setSaveInProgress(false);
      showAlert({
        message: apiRequestError(e, "Something went wrong in our system."),
        severity: "error",
      });
    }
  };

  // Use effect

  useEffect(() => {
    setWindowTitle("Onboarding - Program");
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const initialPageAsync = async () => {
      if (isPageInitialize) {
        return true;
      }

      if (!canReadProgram(userFunctionAccess)) {
        showAlert({ message: "Access Denied", severity: "error" });
        goToRoutePage("Home");
        return;
      }

      if (isNullOrWhiteSpace(programKey)) {
        showAlert({ message: "Program key missing.", severity: "error" });
        goToRoutePage("onboarding");
        return;
      }

      if (programKey === referenceLookups.newRecordDefaultId) {
        if (!canWriteProgram(userFunctionAccess)) {
          showAlert({ message: "Access Denied.", severity: "error" });
          goToRoutePage("onboarding");
          return;
        }

        const tempFormDefaultValue = JSON.parse(JSON.stringify(formDefaultValue));
        tempFormDefaultValue.programKey = referenceLookups.newRecordDefaultId;

        if (!isNullOrWhiteSpace(clientCompanyKey)) {
          try {
            const filterCompanys = clientCompanies.filter((company) => company.clientCompanyKey === clientCompanyKey);

            if (filterCompanys.length > 0) {
              tempFormDefaultValue.clientCompanyKey = filterCompanys[0].clientCompanyKey;
              tempFormDefaultValue.address = filterCompanys[0].address;
            }
          } catch (e) { }
        }

        setInitialFormValues({ ...tempFormDefaultValue });
        setStates(filterCountryStates(referenceLookups.countries, tempFormDefaultValue.address.country));
        setReadonly(false);
        setIsNew(true);
        setIsPageInitialize(true);
        setLoading(false);
        return;
      }

      const apiResponse = await getProgramDetail(programKey);
      if (apiResponse.success) {
        const data = apiResponse.data;

        setFolderId(data.folder.id);
        setInitialFormValues(data.program);
        setProgramKits(data.programKits);
        setStates(filterCountryStates(referenceLookups.countries, data.program.address.country));

        setReadonly(!canWriteProgram(userFunctionAccess));
        if (apiResponse.data.program.enrollmentCount > 0) {
          setLimitedUpdates(true);
        }
        setIsNew(false);
        setIsPageInitialize(true);
        setLoading(false);
      } else {
        showAlert({ message: apiResponse.errorMessage, severity: "error" });
        goToRoutePage("onboarding");
        return;
      }
    };

    initialPageAsync();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [programKey, userFunctionAccess, referenceLookups]);

  return (
    <React.Fragment>
      {loading ? (
        <SplashWait />
      ) : (
        <Screen>
          <SaveBackdrop open={saveInProgress} />

          <ProgramThemeAndMetadataCRUD open={themeEditDialogOpen} programKey={programKey} onCallback={saveThemeAndMetadataResult} />
          <LegacyEnlistmentContentsCURD open={legacyEnlistmentContentEditDialogOpen} programKey={programKey} onCallback={saveLegacyEnlistmentContentResult} />

          <Confirm title="Confirmation" content={`<p>Are you sure you want to delete this kit from the program?</p><p style="text-align:center"><b>${selectedProgramKit.programKit.hsbKitNo} - ${selectedProgramKit.programKit.name} </b></p>`} yesText="DELETE" noText="CANCEL" open={deleteConfirmDialogOpenForProgramKit} callbackOnClose={() => setDeleteConfirmDialogOpenForProgramKit} callbackOnConfirm={deleteConfirmationForProgramKit} />
          
          <ConfirmSelect title="Select Kits" label={"Kit"} content={``} data={newKitLookupValues} yesText="ADD" noText="CANCEL" open={addKitConfirmOpen} multiSelect={true} maxSelect={0} callbackOnSelect={selectNewKit} />

          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Grid container>
                <Grid item xs={10} sm={10} md={10} lg={10}>
                  <Box className={globalStyles.bottomSpacer}>
                    <Breadcrumbs aria-label="breadcrumb" maxItems={getBreadcrumbMaxItems(3)}>
                      <Link color="inherit" onClick={() => goToRoutePage("home")} className={globalStyles.link}>
                        Home
                      </Link>
                      <Link color="inherit" onClick={() => goToRoutePage("onboarding")} className={globalStyles.link}>
                        Onboarding
                      </Link>
                      <Link color="inherit" onClick={() => goToRoutePage("onboarding\\programs")} className={globalStyles.link}>
                        Programs
                      </Link>
                      <Typography color="textPrimary">{isNew ? "New" : !readonly ? "Edit" : "View"}</Typography>
                    </Breadcrumbs>
                  </Box>
                </Grid>
                <Grid item xs={2} sm={2} md={2} lg={2}>
                  {!isNew ? (
                    <Box display="flex" justifyContent="flex-end" width={1}>
                      <Tooltip arrow title="Menu">
                        <Button color="secondary" aria-controls="action-menu" aria-haspopup="true" onClick={handleAnchorElOptionsMenuClick}>
                          <MoreIcon />
                        </Button>
                      </Tooltip>
                      <Menu elevation={2} id="action-menu" className={globalStyles.rightsideMenu} anchorEl={anchorElForOptionsMenu} keepMounted open={Boolean(anchorElForOptionsMenu)} onClose={handleAnchorElOptionsMenuClose}>
                        {!isNew && !saveButtonVisible ? (
                          <ListItem
                            onClick={() => {
                              openThemeEdit();
                              handleAnchorElOptionsMenuClose();
                            }}
                          >
                            <ListItemText primary="Theme & Metadata" />
                            <ListItemIcon className={pageStyles.listItemIcon}>
                              <MetadataIcon color="secondary" />
                            </ListItemIcon>
                          </ListItem>
                        ) : null}

                        {!isNew && !saveButtonVisible ? <Divider /> : null}

                        <ListItem
                          onClick={() => {
                            openExternalLink(appSetting.meshifyUrl + "/admin#/folders/" + folderId);
                            handleAnchorElOptionsMenuClose();
                          }}
                        >
                          <ListItemText primary="Meshify Admin" />
                          <ListItemIcon className={pageStyles.listItemIcon}>
                            <OpenInNewIcon color="secondary" />
                          </ListItemIcon>
                        </ListItem>
                      </Menu>
                    </Box>
                  ) : (
                    <></>
                  )}
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12} sm={12} md={12} lg={9} className={globalStyles.formikContainer}>
              <Paper elevation={0} className={pageStyles.inputPaperContainer}>
                <Grid container>
                  {limitedUpdates ? (
                    <React.Fragment>
                      <Grid item xs={12}>
                        <Alert severity="info">{"Please note that limited updates can be made to this client company since " + initialFormValues.enrollmentCount + " participants have already been enrolled."}</Alert>
                      </Grid>
                      <Grid item xs={12}>
                        <Box p={3}></Box>
                      </Grid>
                    </React.Fragment>
                  ) : null}

                  <Grid item xs={12} className={pageStyles.formContainer}>
                    <Formik
                      innerRef={formReference}
                      initialValues={initialFormValues}
                      validationSchema={formValidationSchema}
                      validateOnMount={true}
                      initialTouched={!isNew ? formDefaultTouchValue : {}}
                      validateOnBlur={true}
                      validateOnChange={true}
                      onSubmit={(values: Program) => {
                        saveProgram(values);
                      }}
                    >
                      {({ values, errors, handleChange, setFieldValue, touched, handleBlur, isSubmitting, validateForm }) => (
                        <Form
                          onChange={() => {
                            setSaveButtonVisible(true);
                          }}
                        >
                          <Grid container spacing={3}>
                            {/* Key Value */}

                            <Grid item xs={12} sm={8} md={6} lg={6}>
                              <TextField autoFocus={isNew} disabled={readonly || limitedUpdates} id="programName" label="Name" value={values.programName}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={touched.programName && Boolean(errors.programName)}
                                helperText={errors.programName && touched.programName ? errors.programName : null} />
                            </Grid>

                            {!isNew ? (
                              <Grid item xs={9} sm={4} md={6} lg={2}>
                                <TextField disabled={true} id="programCode" name="programCode" label="Program Code" value={values.programCode} onChange={handleChange} onBlur={handleBlur} error={touched.programCode && Boolean(errors.programCode)} helperText={errors.programCode && touched.programCode ? errors.programCode : null} />
                              </Grid>
                            ) : null}

                            <Grid item xs={12} sm={8} md={6} lg={4}>
                              <FormControl disabled={readonly || limitedUpdates} className={globalStyles.formControl} fullWidth={true} error={Boolean(errors.clientCompanyKey)}>
                                <InputLabel id="parent-id-label" shrink={true}>
                                  Company
                                </InputLabel>
                                <Select native labelId="client-company-key-label" id="clientCompanyKey" name="clientCompanyKey" value={values.clientCompanyKey} onChange={handleChange} label="Parent Company" error={touched.clientCompanyKey && Boolean(errors.clientCompanyKey)}>
                                  {initialFormValues.programKey === referenceLookups.newRecordDefaultId ? (
                                    <React.Fragment>
                                      <option value=""></option>
                                    </React.Fragment>
                                  ) : null}

                                  {referenceLookups.clientCompanies.map((clientCompany, index) => (
                                    <option value={clientCompany.clientCompanyKey} key={index}>
                                      {clientCompany.name}
                                    </option>
                                  ))}
                                </Select>
                                <FormHelperText error>{errors.clientCompanyKey}</FormHelperText>
                              </FormControl>
                            </Grid>


                            <Grid item xs={6} sm={4} md={3} lg={2}>
                              <FormControl className={globalStyles.formControl} fullWidth={true}>
                                <FormLabel component="legend" className={globalStyles.checkBoxLabel}>
                                  Tax Exempt
                                </FormLabel>
                                <FormGroup>
                                  <FormControlLabel
                                    control={
                                      <Switch
                                        disabled={readonly}
                                        id="taxExempt"
                                        name="taxExempt"
                                        checked={values.taxExempt}
                                        onChange={(ev, checked) => {
                                          setFieldValue("taxExempt", checked);
                                        }}
                                      />
                                    }
                                    label={values.taxExempt ? "Yes" : "No"}
                                    labelPlacement="end"
                                  />
                                </FormGroup>
                              </FormControl>
                            </Grid>

                            <Grid item xs={6} sm={4} md={3} lg={2}>
                              <FormControl className={globalStyles.formControl} fullWidth={true}>
                                <FormLabel className={globalStyles.checkBoxLabel}>Active</FormLabel>
                                <FormGroup>
                                  <FormControlLabel
                                    control={
                                      <Switch
                                        disabled={readonly}
                                        id="active"
                                        name="active"
                                        checked={values.active}
                                        onChange={(ev, checked) => {
                                          setFieldValue("active", checked);
                                        }}
                                      />
                                    }
                                    label={values.active ? "Yes" : "No"}
                                    labelPlacement="end"
                                  />
                                </FormGroup>
                              </FormControl>
                            </Grid>

                            {/* Address */}

                            <Grid item xs={12}>
                              <Box className={globalStyles.topic}>
                                {addressValidationInProgress ? <LinearProgress color="secondary" /> : null}
                                <Grid container>
                                  <Grid item xs={8}>
                                    Location
                                  </Grid>
                                  <Grid item xs={4}>
                                    <Box display="flex" justifyContent="flex-end" width={1}>
                                      {!readonly ? (
                                        <React.Fragment>
                                          {!addressValidationInProgress ? (
                                            <>
                                              {showAddressValidationButton() ? (
                                                <Tooltip arrow title="Validate address">
                                                  <IconButton onClick={checkAddress}>
                                                    <ValidateAddressIcon color="secondary" />
                                                  </IconButton>
                                                </Tooltip>
                                              ) : null}
                                            </>
                                          ) : null}

                                          <Typography variant="body1">&nbsp;&nbsp;&nbsp;</Typography>
                                          <Tooltip arrow title="Clear address">
                                            <IconButton onClick={resetAddressFields}>
                                              <ClearAllIcon color="error" />
                                            </IconButton>
                                          </Tooltip>
                                        </React.Fragment>
                                      ) : null}
                                    </Box>
                                  </Grid>
                                </Grid>
                              </Box>
                            </Grid>

                            {programAddressResultOutput.show ? (
                              <Grid item xs={12}>
                                <DisplayAddressResult addressResultOutput={programAddressResultOutput} currentAddress={values.address} callbackOnClose={closeAddressMessageStatus} useThisAddressButton={true} callbackOnUseThisAddress={useThisAddress} overrideValidationAddress={false} callbackOnOverrideValidationAddress={() => { }} />
                              </Grid>
                            ) : null}

                            <Grid item xs={12} sm={6} md={6} lg={6}>
                              <TextField
                                disabled={readonly}
                                id="address.addressline1"
                                name="address.addressline1"
                                label="Address Line 1"
                                value={values.address.addressline1}
                                onChange={(e) => {
                                  handleChange(e);
                                  handleAddressChanged();
                                }}
                                onBlur={handleBlur}
                                error={getIn(errors, "address.addressline1")}
                                helperText={getIn(errors, "address.addressline1")}
                              />
                            </Grid>

                            <Grid item xs={12} sm={6} md={6} lg={6}>
                              <TextField
                                disabled={readonly}
                                id="address.addressline2"
                                name="address.addressline2"
                                label="Address Line 2"
                                value={values.address.addressline2}
                                onChange={(e) => {
                                  handleChange(e);
                                  handleAddressChanged();
                                }}
                                onBlur={handleBlur}
                              />
                            </Grid>

                            <Grid item xs={12} sm={6} md={6} lg={3}>
                              <TextField
                                disabled={readonly}
                                id="address.locality1"
                                name="address.locality1"
                                label="City"
                                value={values.address.locality1}
                                onChange={(e) => {
                                  handleChange(e);
                                  handleAddressChanged();
                                }}
                                onBlur={handleBlur}
                                error={Boolean(getIn(errors, "address.locality1"))}
                                helperText={getIn(errors, "address.locality1")}
                              />
                            </Grid>

                            <Grid item xs={6} sm={6} md={3} lg={3}>
                              <FormControl className={globalStyles.formControl} fullWidth={true} error={Boolean(getIn(errors, "address.region1"))}>
                                <InputLabel id="state-label" shrink={true}>
                                  State
                                </InputLabel>
                                <Select
                                  disabled={readonly}
                                  native
                                  labelId="state-label"
                                  id="address.region1"
                                  name="address.region1"
                                  value={values.address.region1}
                                  onChange={(e) => {
                                    handleChange(e);
                                    handleAddressChanged();
                                  }}
                                  label="State"
                                  error={Boolean(getIn(touched, "address.region1") && getIn(errors, "address.region1"))}
                                >
                                  <option value={""}></option>
                                  {states.map((state, index) => (
                                    <option value={state.code} key={index}>
                                      {state.code}-{state.name}
                                    </option>
                                  ))}
                                </Select>
                                <FormHelperText error>{getIn(errors, "address.region1")}</FormHelperText>
                              </FormControl>
                            </Grid>

                            <Grid item xs={6} sm={6} md={3} lg={3}>
                              <TextField
                                disabled={readonly}
                                id="address.postalCode"
                                name="address.postalCode"
                                label="Postal Code"
                                value={values.address.postalCode}
                                onChange={(e) => {
                                  handleChange(e);
                                  handleAddressChanged();
                                }}
                                onBlur={handleBlur}
                                error={Boolean(getIn(errors, "address.postalCode"))}
                                helperText={getIn(errors, "address.postalCode")}
                              />
                            </Grid>

                            <Grid item xs={12} sm={6} md={6} lg={3}>
                              <FormControl className={globalStyles.formControl} fullWidth={true} error={Boolean(getIn(errors, "address.country"))}>
                                <InputLabel id="country-label" shrink={true}>
                                  Country
                                </InputLabel>
                                <Select
                                  disabled={readonly}
                                  native
                                  labelId="country-label"
                                  id="address.country"
                                  name="address.country"
                                  value={values.address.country}
                                  onChange={(e) => {
                                    handleAddressChanged();
                                    handleChange(e);
                                    handleCountryChanged(e);
                                  }}
                                  label="Country"
                                  error={Boolean(getIn(touched, "address.country") && getIn(errors, "address.country"))}
                                >
                                  <option value={""}></option>

                                  {referenceLookups.countries.map((country, index) => (
                                    <option value={country.code} key={index}>
                                      {country.name}
                                    </option>
                                  ))}
                                </Select>
                                <FormHelperText error>{getIn(errors, "address.country")}</FormHelperText>
                              </FormControl>
                            </Grid>

                            {/* Enlistment */}

                            <Grid item xs={12}>
                              <Box className={globalStyles.topic}>Enlistment</Box>
                            </Grid>

                            {values.enableParentEnlistment ? (
                              <React.Fragment>
                                <Grid item xs={12} sm={4} md={3} lg={3}>
                                  <FormControl className={globalStyles.formControl} fullWidth={true}>
                                    <FormLabel component="legend" className={globalStyles.checkBoxLabel}>
                                      Enable Enlistment?
                                    </FormLabel>
                                    <FormGroup>
                                      <FormControlLabel
                                        control={
                                          <Switch
                                            disabled={readonly}
                                            id="enableEnlistment"
                                            name="enableEnlistment"
                                            checked={values.enableEnlistment}
                                            onChange={(ev, checked) => {
                                              setFieldValue("enableEnlistment", checked);
                                            }}
                                          />
                                        }
                                        label={values.enableEnlistment ? "Yes" : "No"}
                                        labelPlacement="end"
                                      />
                                    </FormGroup>
                                  </FormControl>
                                </Grid>
                                {values.enableEnlistment ? (
                                  <React.Fragment>
                                    <Grid item xs={12} sm={4} md={3} lg={3}>
                                      <FormControl className={globalStyles.formControl} fullWidth={true}>
                                        <FormLabel component="legend" className={globalStyles.checkBoxLabel}>
                                          Agreement Confirmation?
                                        </FormLabel>
                                        <FormGroup>
                                          <FormControlLabel
                                            control={
                                              <Switch
                                                disabled={readonly}
                                                id="showAgreementConfirmation"
                                                name="showAgreementConfirmation"
                                                checked={values.showAgreementConfirmation}
                                                onChange={(ev, checked) => {
                                                  setFieldValue("showAgreementConfirmation", checked);
                                                }}
                                              />
                                            }
                                            label={values.showAgreementConfirmation ? "Yes" : "No"}
                                            labelPlacement="end"
                                          />
                                        </FormGroup>
                                      </FormControl>
                                    </Grid>

                                    <Grid item xs={12} sm={4} md={3} lg={3}>
                                      <FormControl className={globalStyles.formControl} fullWidth={true}>
                                        <FormLabel component="legend" className={globalStyles.checkBoxLabel}>
                                          Enable Subscription
                                        </FormLabel>
                                        <FormGroup>
                                          <FormControlLabel
                                            control={
                                              <Switch
                                                disabled={readonly}
                                                id="showAgreementConfirmation"
                                                name="showAgreementConfirmation"
                                                checked={values.enableSubscriptionBilling}
                                                onChange={(ev, checked) => {
                                                  setFieldValue("enableSubscriptionBilling", checked);
                                                  if (checked) {
                                                    setFieldValue("showKits", false);
                                                  }
                                                }}
                                              />
                                            }
                                            label={values.enableSubscriptionBilling ? "Yes" : "No"}
                                            labelPlacement="end"
                                          />
                                        </FormGroup>
                                      </FormControl>
                                    </Grid>

                                    <Grid item xs={12} sm={4} md={3} lg={3}>
                                      <FormControl className={globalStyles.formControl} fullWidth={true}>
                                        <FormLabel component="legend" className={globalStyles.checkBoxLabel}>
                                          Show Kits
                                        </FormLabel>
                                        <FormGroup>
                                          <FormControlLabel
                                            control={
                                              <Switch
                                                disabled={readonly || values.enableSubscriptionBilling}
                                                id="showKits"
                                                name="showKits"
                                                checked={values.showKits}
                                                onChange={(ev, checked) => {
                                                  setFieldValue("showKits", checked);
                                                }}
                                              />
                                            }
                                            label={values.showKits ? "Yes" : "No"}
                                            labelPlacement="end"
                                          />
                                        </FormGroup>
                                      </FormControl>
                                    </Grid>

                                    <Grid item xs={12} sm={6} md={4} lg={6}>
                                      <FormControl fullWidth={true}>
                                        <InputLabel>AEM Site Id</InputLabel>
                                        <Input
                                          required
                                          disabled={readonly}
                                          id="aemSiteId"
                                          value={values.aemSiteId}
                                          onChange={handleChange}
                                          error={touched.aemSiteId && Boolean(errors.aemSiteId)}
                                          endAdornment={
                                            <InputAdornment position="end">
                                              {!readonly ? (
                                                <React.Fragment>
                                                  <Tooltip arrow title="Clear Id">
                                                    <IconButton onClick={() => clearAEMSiteId()}>
                                                      <ClearIcon color="error" />
                                                    </IconButton>
                                                  </Tooltip>
                                                  <Tooltip arrow title="Generate New Unique Id">
                                                    <IconButton
                                                      onClick={() => {
                                                        generateNewAEMSiteId();
                                                      }}
                                                    >
                                                      <FiberNewIcon />
                                                    </IconButton>
                                                  </Tooltip>
                                                </React.Fragment>
                                              ) : null}
                                            </InputAdornment>
                                          }
                                        />
                                        <FormHelperText error={true}>{errors.aemSiteId && touched.aemSiteId ? errors.aemSiteId : null}</FormHelperText>
                                      </FormControl>
                                    </Grid>

                                    <Grid item xs={12} sm={3} md={4} lg={3}>
                                      <TextField disabled={readonly} id="routingName" label="AEM Site Prefix" value={values.routingName} onChange={handleChange} onBlur={handleBlur} error={touched.routingName && Boolean(errors.routingName)} helperText={errors.routingName && touched.routingName ? errors.routingName : null} />
                                    </Grid>

                                    <Grid item xs={12} sm={3} md={3} lg={3}>
                                      <TextField disabled={readonly} id="maxAccountCanBeRegister" label="Max Location Register" value={values.maxAccountCanBeRegister} type="number" onChange={handleChange} onBlur={handleBlur} error={touched.maxAccountCanBeRegister && Boolean(errors.maxAccountCanBeRegister)} helperText={errors.maxAccountCanBeRegister && touched.maxAccountCanBeRegister ? errors.maxAccountCanBeRegister : null} />
                                    </Grid>

                                    {values.showInEnlistmentProductLookup ? (
                                      <React.Fragment>

                                        <Grid item xs={12} sm={4} md={4} lg={3} className={pageStyles.hideItem}>
                                          <FormControl className={globalStyles.formControl} fullWidth={true}>
                                            <FormLabel component="legend" className={globalStyles.checkBoxLabel}>
                                              Site manage by AEM?
                                            </FormLabel>
                                            <FormGroup>
                                              <FormControlLabel
                                                control={
                                                  <Switch
                                                    disabled={readonly}
                                                    id="enlistmentSiteManageByAEM"
                                                    name="enlistmentSiteManageByAEM"
                                                    checked={values.enlistmentSiteManageByAEM}
                                                    onChange={(ev, checked) => {
                                                      setFieldValue("enlistmentSiteManageByAEM", checked);
                                                    }}
                                                  />
                                                }
                                                label={values.enlistmentSiteManageByAEM ? "Yes" : "No"}
                                                labelPlacement="end"
                                              />
                                            </FormGroup>
                                          </FormControl>
                                        </Grid>

                                        <Grid item xs={12} sm={4} md={3} lg={3} className={pageStyles.hideItem}>
                                          <FormControl className={globalStyles.formControl} fullWidth={true}>
                                            <FormLabel component="legend" className={globalStyles.checkBoxLabel}>
                                              Is Customer Facing Site
                                            </FormLabel>
                                            <FormGroup>
                                              <FormControlLabel
                                                control={
                                                  <Switch
                                                    disabled={readonly}
                                                    id="isEnlistmentCustomerSite"
                                                    name="isEnlistmentCustomerSite"
                                                    checked={values.isEnlistmentCustomerSite}
                                                    onChange={(ev, checked) => {
                                                      setFieldValue("isEnlistmentCustomerSite", checked);
                                                    }}
                                                  />
                                                }
                                                label={values.isEnlistmentCustomerSite ? "Yes" : "No"}
                                                labelPlacement="end"
                                              />
                                            </FormGroup>
                                          </FormControl>
                                        </Grid>

                                        <Grid item xs={12} sm={4} md={3} lg={3} className={pageStyles.hideItem}>
                                          <FormControl className={globalStyles.formControl} fullWidth={true}>
                                            <FormLabel component="legend" className={globalStyles.checkBoxLabel}>
                                              Enable Auto Enroll
                                            </FormLabel>
                                            <FormGroup>
                                              <FormControlLabel
                                                control={
                                                  <Switch
                                                    disabled={true}
                                                    id="autoEnroll"
                                                    name="autoEnroll"
                                                    checked={values.autoEnroll}
                                                    onChange={(ev, checked) => {
                                                      setFieldValue("autoEnroll", checked);
                                                    }}
                                                  />
                                                }
                                                label={values.autoEnroll ? "Yes" : "No"}
                                                labelPlacement="end"
                                              />
                                            </FormGroup>
                                          </FormControl>
                                        </Grid>

                                        <Grid item xs={12} sm={4} md={3} lg={3} className={pageStyles.hideItem}>
                                          <FormControl className={globalStyles.formControl} fullWidth={true}>
                                            <FormLabel component="legend" className={globalStyles.checkBoxLabel}>
                                              Show For Registration
                                            </FormLabel>
                                            <FormGroup>
                                              <FormControlLabel
                                                control={
                                                  <Switch
                                                    disabled={readonly}
                                                    id="showInEnlistmentProductLookup"
                                                    name="showInEnlistmentProductLookup"
                                                    checked={values.showInEnlistmentProductLookup}
                                                    onChange={(ev, checked) => {
                                                      setFieldValue("showInEnlistmentProductLookup", checked);
                                                    }}
                                                  />
                                                }
                                                label={values.showInEnlistmentProductLookup ? "Yes" : "No"}
                                                labelPlacement="end"
                                              />
                                            </FormGroup>
                                          </FormControl>
                                        </Grid>

                                        <Grid item xs={12} sm={4} md={3} lg={3} className={pageStyles.hideItem}>
                                          <TextField disabled={readonly} id="enlistmentProgramGroupCode" label="Group Name" value={values.enlistmentProgramGroupCode} onChange={handleChange} onBlur={handleBlur} error={touched.enlistmentProgramGroupCode && Boolean(errors.enlistmentProgramGroupCode)} helperText={errors.enlistmentProgramGroupCode && touched.enlistmentProgramGroupCode ? errors.enlistmentProgramGroupCode : null} />
                                        </Grid>

                                        <Grid item xs={12} sm={4} md={3} lg={3} className={pageStyles.hideItem}>
                                          <TextField disabled={readonly} id="totalPilotAccountForRegistration" label="Total Pilot Locations" value={values.totalPilotAccountForRegistration} type="number" onChange={handleChange} onBlur={handleBlur} error={touched.totalPilotAccountForRegistration && Boolean(errors.totalPilotAccountForRegistration)} helperText={errors.totalPilotAccountForRegistration && touched.totalPilotAccountForRegistration ? errors.totalPilotAccountForRegistration : null} />
                                        </Grid>

                                      </React.Fragment>
                                    ) : null}

                                    {values.showInEnlistmentProductLookup ? (
                                      <React.Fragment>
                                        <Grid item xs={12} sm={8} md={6} lg={6} className={pageStyles.hideItem}>
                                          <FormControl required disabled={readonly} className={globalStyles.formControl} fullWidth={true} error={Boolean(errors.meshifyDomainName)}>
                                            <InputLabel id="parent-id-label" shrink={true}>
                                              Meshify Domain Name
                                            </InputLabel>
                                            <Select native labelId="meshify_domain-name-label" id="meshifyDomainName" name="meshifyDomainName" value={values.meshifyDomainName} onChange={handleChange} label="Parent Company" error={touched.meshifyDomainName && Boolean(errors.meshifyDomainName)}>
                                              <option value={""}></option>
                                              {referenceLookups.domains.length > 0
                                                ? referenceLookups.domains.map((domain, index) => (
                                                  <option value={domain.fqdn} key={index}>
                                                    {domain.fqdn}
                                                  </option>
                                                ))
                                                : null}
                                            </Select>
                                            <FormHelperText error>{errors.meshifyDomainName}</FormHelperText>
                                          </FormControl>
                                        </Grid>

                                        {programKits.length > 0 ? (
                                          <Grid item xs={12} sm={6} md={6} lg={6}>
                                            <FormControl disabled={readonly} className={globalStyles.formControl} fullWidth={true} error={Boolean(errors.defaultPackageId)}>
                                              <InputLabel id="parent-id-label" shrink={true}>
                                                Default Kit
                                              </InputLabel>
                                              <Select native labelId="default-package-id-label" id="defaultPackageId" name="defaultPackageId" value={values.defaultPackageId} onChange={handleChange} label="Parent Company" error={touched.defaultPackageId && Boolean(errors.defaultPackageId)}>
                                                <option value={0}></option>
                                                {programKits.length > 0
                                                  ? programKits.map((kit, index) => (
                                                    <option value={kit.hsbKitNo} key={index}>
                                                      {kit.name}
                                                    </option>
                                                  ))
                                                  : null}
                                              </Select>
                                              <FormHelperText error>{errors.defaultPackageId}</FormHelperText>
                                              <Typography variant="caption">(Leave blank for no default)</Typography>
                                            </FormControl>
                                          </Grid>
                                        ) : null}


                                      </React.Fragment>
                                    ) : null}
                                  </React.Fragment>
                                ) : null}
                              </React.Fragment>
                            ) : (
                              <Grid item xs={12}>
                                <Alert severity="warning">
                                  Enlistment is switched off @ company level.&nbsp;
                                  {!isNew && !saveButtonVisible && canWriteCompany(userFunctionAccess) ? (
                                    <React.Fragment>
                                      Click
                                      <Link
                                        href="#"
                                        onClick={() => {
                                          goToCompany();
                                        }}
                                      >
                                        &nbsp;HERE&nbsp;
                                      </Link>
                                      to enable the enlistment
                                    </React.Fragment>
                                  ) : null}
                                </Alert>
                              </Grid>
                            )}

                            {/* Other */}

                            <Grid item xs={12}>
                              <Box className={globalStyles.topic}>External Apps</Box>
                            </Grid>
                            <Grid item xs={12} sm={4} md={4} lg={3}>
                              <FormControl className={globalStyles.formControl} fullWidth={true}>
                                <FormLabel className={globalStyles.checkBoxLabel}>Send to PS Finance</FormLabel>
                                <FormGroup>
                                  <FormControlLabel
                                    control={
                                      <Switch
                                        disabled={readonly}
                                        id="sendToPSFIN"
                                        name="sendToPSFIN"
                                        checked={values.sendToPSFIN}
                                        onChange={(ev, checked) => {
                                          setFieldValue("sendToPSFIN", checked);
                                        }}
                                      />
                                    }
                                    label={values.sendToPSFIN ? "Yes" : "No"}
                                    labelPlacement="end"
                                  />
                                </FormGroup>
                              </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={8} md={8} lg={5}>
                              <TextField disabled={readonly} id="shopifyLandingPage" name="shopifyLandingPage" label="Shopify Landing Page" value={values.shopifyLandingPage} onChange={handleChange} onBlur={handleBlur} error={touched.shopifyLandingPage && Boolean(errors.shopifyLandingPage)} helperText={errors.shopifyLandingPage && touched.shopifyLandingPage ? errors.shopifyLandingPage : null} />
                            </Grid>

                            {/* Kits */}

                            {!isNew ? (
                              <>
                                <Grid item xs={12}>
                                  <Box className={globalStyles.topic}>
                                    <Grid container>
                                      <Grid item xs={8}>
                                        Kits
                                      </Grid>
                                      <Grid item xs={4}>
                                        <Box display="flex" justifyContent="flex-end" width={1}>
                                          {!readonly && !saveButtonVisible && !isNew ? (
                                            <Tooltip arrow title="Add new kit">
                                              <IconButton onClick={handleAddNewKit}>
                                                <AddIcon color="secondary" />
                                              </IconButton>
                                            </Tooltip>
                                          ) : null}
                                        </Box>
                                      </Grid>
                                    </Grid>
                                  </Box>
                                </Grid>

                                <Grid item xs={12}>
                                  <TableContainer className={pageStyles.kitContainer}>
                                    <Table stickyHeader >
                                      <TableHead>
                                        <TableRow>
                                          <TableCell colSpan={2}>Kit</TableCell>
                                          <TableCell>Subscription Billing</TableCell>
                                          <TableCell>Replacement Kit</TableCell>
                                          <TableCell>Active</TableCell>
                                          <TableCell align="right">Enrolled#</TableCell>
                                          <TableCell>&nbsp;</TableCell>
                                        </TableRow>
                                      </TableHead>
                                      <TableBody>
                                        {
                                          programKits.map((kit, index) =>
                                          (
                                            <TableRow key={index}>
                                              <TableCell>{kit.hsbKitNo}</TableCell>
                                              <TableCell>
                                                {
                                                  !saveButtonVisible ?
                                                    <Box onClick={() => handlePackageKitItemClick(kit)}>
                                                      {kit.name}
                                                    </Box>
                                                    :
                                                    kit.name
                                                }
                                              </TableCell>
                                              <TableCell>
                                                {kit.subscriptionBilling ? "Yes" : "No"}
                                              </TableCell>
                                              <TableCell>
                                                {kit.replacementKit ? "Yes" : "No"}
                                              </TableCell>
                                              <TableCell>
                                                {kit.active ? "Yes" : "No"}
                                              </TableCell>
                                              <TableCell align="right">{kit.count}</TableCell>
                                              {
                                                !readonly && !saveButtonVisible && kit.hsbKitNo > 0 && !kit.used && canWriteProgram(userFunctionAccess) ?
                                                  <TableCell align="right" style={{ width: '30px' }}>
                                                    <DeleteIcon color="error"
                                                      onClick={() => showDeleteConfirmationForProgramKit(index, kit)} />
                                                  </TableCell>
                                                  :
                                                  <TableCell></TableCell>
                                              }

                                            </TableRow>
                                          ))
                                        }
                                      </TableBody>
                                    </Table>
                                  </TableContainer>
                                </Grid>
                              </>
                            )
                              : null
                            }

                            <Grid item xs={12}>
                              <Box p={7}></Box>
                            </Grid>
                          </Grid>

                          <Box p={3} pt={3} display="flex" justifyContent="center">
                            {!readonly ? (
                              <Button
                                id="button-save"
                                color="secondary"
                                className={globalStyles.buttonDefault}
                                disabled={!saveButtonVisible}
                                onClick={() => {
                                  formReference.current.handleSubmit();
                                }}
                                variant="contained"
                              >
                                {isNew ? "Save" : "Update"}
                              </Button>
                            ) : null}
                            <Button id="button-cancel" color="default" className={globalStyles.buttonDefault} onClick={() => goToRoutePage("onboarding/programs")}>
                              <u>{!readonly ? "Cancel" : "Back"}</u>
                            </Button>
                          </Box>
                        </Form>
                      )}
                    </Formik>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            <Grid item xs={12} sm={12} md={12} lg={3}>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6} md={6} lg={12}>
                  <Card elevation={0} className={globalStyles.rightCardContainer}>
                    <CardHeader title="" subheader="INCLUDES" />
                    <CardContent>
                      <Grid container>

                        <Grid item xs={6}  >
                          <Box display="flex" flexDirection="column" justifyItems='center' alignItems='center' textAlign="center" >
                            <Box>
                              <Typography variant="h6" className="count"  >
                                {initialFormValues.enrollmentCount}
                              </Typography>
                            </Box>
                            <Box>
                              <Typography variant="caption" className={globalStyles.toggleFontColor}>
                                Enrollments
                              </Typography>
                            </Box>
                          </Box>
                        </Grid>
                        <Grid item xs={6}  >
                          <Box display="flex" flexDirection="column" justifyItems='center' alignItems='center' textAlign="center" >
                            <Box>
                              <Typography variant="h6" className="count"  >
                                {programKits.length}
                              </Typography>
                            </Box>
                            <Box>
                              <Typography variant="caption" className={globalStyles.toggleFontColor}>
                                Kits
                              </Typography>
                            </Box>
                          </Box>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                  <Box p="3">&nbsp;</Box>
                </Grid>
                <Grid item xs={12} sm={6} md={6} lg={12}>
                  <Card elevation={0} className={globalStyles.rightCardContainer}>
                    <CardHeader title="" subheader="AUDIT" />
                    <CardContent style={{ minHeight: "250px" }}>
                      <Grid container>
                        <Grid item xs={12}  >
                          <Box display="flex" flexDirection="column" justifyItems='center' alignItems='center' textAlign="center" >
                            <Box>
                              <Typography variant="h6" className="text"  >
                                {!isNew && initialFormValues.lastModifiedBy !== "" ? initialFormValues.lastModifiedBy : " "}
                              </Typography>
                            </Box>
                            <Box>
                              <Typography variant="caption" className={globalStyles.toggleFontColor}>
                                Last Update By
                              </Typography>
                            </Box>
                          </Box>
                        </Grid>
                        <Grid item xs={12}  >&nbsp;</Grid>
                        <Grid item xs={12}  >
                          <Box display="flex" flexDirection="column" justifyItems='center' alignItems='center' textAlign="center" >
                            <Box>
                              <Typography variant="h6" className="text"  >
                                {!isNew && initialFormValues.lastModifiedDateTime !== "" ? <Moment date={initialFormValues.lastModifiedDateTime} format="M/D/YYYY - h:mm:ss A z" /> : " "}
                              </Typography>
                            </Box>
                            <Box>
                              <Typography variant="caption" className={globalStyles.toggleFontColor}>
                                Last Updated On
                              </Typography>
                            </Box>
                          </Box>
                        </Grid>

                        {initialFormValues.readyToEnroll ? (
                          <>
                            <Grid item xs={12}>
                              <Box p={3}></Box>
                            </Grid>

                            <Grid item xs={12}  >
                              <Box display="flex" flexDirection="column" justifyItems='center' alignItems='center' textAlign="center" >
                                <Box>
                                  <Typography variant="h6" className="text"  >
                                    {initialFormValues.readyToEnrollApprovedBy !== "" ? initialFormValues.readyToEnrollApprovedBy : "Unknown"}
                                  </Typography>
                                </Box>
                                <Box>
                                  <Typography variant="caption" className={globalStyles.toggleFontColor}>
                                    Approved By
                                  </Typography>
                                </Box>
                              </Box>
                            </Grid>
                            <Grid item xs={12}  >&nbsp;</Grid>
                            <Grid item xs={12}  >
                              <Box display="flex" flexDirection="column" justifyItems='center' alignItems='center' textAlign="center" >
                                <Box>
                                  <Typography variant="h6" className="text"  >
                                    {initialFormValues.readyToEnrollApprovedDate !== "" ? <Moment date={initialFormValues.readyToEnrollApprovedDate} format="MM/DD/YYYY hh:mm:ss" /> : "Unknown"}
                                  </Typography>
                                </Box>
                                <Box>
                                  <Typography variant="caption" className={globalStyles.toggleFontColor}>
                                    Approved On
                                  </Typography>
                                </Box>
                              </Box>
                            </Grid>
                          </>
                        ) : null}
                      </Grid>
                    </CardContent>
                  </Card>
                  <Box p="3">&nbsp;</Box>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Screen>
      )}
    </React.Fragment>
  );
};

const LocalPageStyles = makeStyles((theme: Theme) =>
  createStyles({
    selectEmpty: {
      marginTop: theme.spacing(2),
    },
    hideItem: {
      display: 'none'
    },
    inputMinWidth: {
      maxWidth: "250px",
    },
    formContainer: {
      maxWidth: "1400px",
    },
    countContainer: {
      justifyItems: "center",
    },
    listItemIcon: {
      justifyContent: "flex-end",
    },
    inputPaperContainer: {
      padding: theme.spacing(0),
      backgroundColor: "transparent",
    },
    gridFormContainer: {
      width: "100%",
    },
    chipRoot: {
      width: "100%",
      padding: 0,
      borderRadius: theme.spacing(1),
      "& .MuiChip-label": {
        paddingTop: "4px",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        width: "70%",
      },
    },
    gridDeleteChip: {
      "& .MuiChip-deleteIcon ": {
        color: theme.palette.error.main,
      },
    },
    kitContainer: {
      maxHeight: 500,
      [theme.breakpoints.down("sm")]: {
        width: '95vw',
      },
      "& .MuiTableRow-head": {
        backgroundColor: theme.palette.type === "dark" ? theme.palette.grey[800] : theme.palette.grey[50],
      },
      "& .MuiTableCell-stickyHeader": {
        backgroundColor: theme.palette.type === "dark" ? theme.palette.grey[800] : theme.palette.grey[50],
        fontSize: '14px',
        verticalAlign: 'top',
      }
    },
  })
);

export default ProgramCRUD;